import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import Button from '@mui/material/Button';
import api, { formatApiError } from '../api';

function classifyChannel(order) {
  const hay = [
    order?.hotel_name,
    order?.hotel_id,
    order?.room_number,
    order?.source,
    order?.channel,
    order?.order_source,
    order?.customer_type,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (
    hay.includes('hotel') ||
    order?.hotel_id ||
    order?.hotel_name ||
    order?.room_number
  ) {
    return 'hotel';
  }
  return 'local';
}

function channelLabel(ch) {
  return ch === 'hotel' ? 'Hotel guest' : 'Local customer';
}

function money(n, currency = 'AED') {
  const v = Number(n);
  if (Number.isNaN(v)) return '—';
  return `${currency} ${v.toFixed(2)}`;
}

export default function SalesPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all | hotel | local

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/vendor/orders', {
        params: { limit: 100, summary: false },
      });
      const list = Array.isArray(res.data) ? res.data : res.data?.orders || [];
      setOrders(list);
    } catch (err) {
      setError(formatApiError(err, 'Could not load sales'));
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => {
    return orders
      .map((o) => ({
        ...o,
        _channel: classifyChannel(o),
        _item:
          o.meal_name ||
          o.invoice?.lines?.[0]?.description ||
          o.product_name ||
          'Item',
      }))
      .filter((o) => (filter === 'all' ? true : o._channel === filter));
  }, [orders, filter]);

  const counts = useMemo(() => {
    let hotel = 0;
    let local = 0;
    for (const o of orders) {
      if (classifyChannel(o) === 'hotel') hotel += 1;
      else local += 1;
    }
    return { hotel, local, all: orders.length };
  }, [orders]);

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={900}>
            Sales
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Products sold via hotel guests or local customers
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            select
            size="small"
            label="Channel"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 160 }, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
          >
            <MenuItem value="all">All ({counts.all})</MenuItem>
            <MenuItem value="hotel">Hotel ({counts.hotel})</MenuItem>
            <MenuItem value="local">Local ({counts.local})</MenuItem>
          </TextField>
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading} size={isMobile ? 'small' : 'medium'}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
        <strong>Hotel</strong> = guest ordered through a hotel / room channel.{' '}
        <strong>Local</strong> = app, maps, or direct WhatsApp customer. Tagging improves as
        marketplace orders land with hotel metadata.
      </Alert>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No sales yet. When guests order your products, they will show here by channel.
        </Alert>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {rows.map((o) => (
            <Paper
              key={o.id || `${o.created_at}-${o._item}`}
              variant="outlined"
              sx={{ p: 1.75, borderRadius: 2 }}
            >
              <Stack spacing={0.75}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                  <Typography fontWeight={800} sx={{ minWidth: 0 }}>
                    {o._item}
                  </Typography>
                  <Typography fontWeight={800} whiteSpace="nowrap">
                    {money(o.total ?? o.total_amount, o.currency || 'AED')}
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {o.created_at ? new Date(o.created_at).toLocaleString() : '—'}
                  {o.quantity != null ? ` · Qty ${o.quantity}` : ''}
                </Typography>
                {(o.hotel_name || o.room_number) && (
                  <Typography variant="caption" color="text.secondary">
                    {[o.hotel_name, o.room_number ? `Room ${o.room_number}` : null]
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                )}
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    label={channelLabel(o._channel)}
                    color={o._channel === 'hotel' ? 'primary' : 'default'}
                    sx={{ fontWeight: 700 }}
                  />
                  <Chip size="small" label={o.status || '—'} sx={{ fontWeight: 700 }} />
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'auto' }}>
          <Table size="small" sx={{ minWidth: 560 }}>
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Item</TableCell>
                <TableCell>Channel</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((o) => (
                <TableRow key={o.id || `${o.created_at}-${o._item}`} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {o.created_at ? new Date(o.created_at).toLocaleString() : '—'}
                    </Typography>
                    {(o.hotel_name || o.room_number) && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {[o.hotel_name, o.room_number ? `Room ${o.room_number}` : null]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={700}>{o._item}</Typography>
                    {o.quantity != null && (
                      <Typography variant="caption" color="text.secondary">
                        Qty {o.quantity}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={channelLabel(o._channel)}
                      color={o._channel === 'hotel' ? 'primary' : 'default'}
                      sx={{ fontWeight: 700 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={o.status || '—'} sx={{ fontWeight: 700 }} />
                  </TableCell>
                  <TableCell align="right">
                    {money(o.total ?? o.total_amount, o.currency || 'AED')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
